import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import { FooterInfoItem, InfoCard } from '@components';
import { useDirection } from '@hooks';
import { Trans, useTrans } from '@libs/i18n';
import {
  ArrowRightIcon,
  FileDashedIcon,
  PresentationChartIcon,
  ShieldWarningIcon,
} from '@phosphor-icons/react';

export const AssessmentsOnboarding: FC = observer(() => {
  const { t } = useTrans();
  const { isRtl } = useDirection();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.paper',
        borderRadius: 4,
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.25rem', mt: 3 }}>
          <Trans i18nKey="assessments.onboarding.title" />
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 400,
            fontSize: '1rem',
            textAlign: 'center',
            mt: 1.5,
            color: 'text.tertiary',
          }}
        >
          <Trans i18nKey="assessments.onboarding.description" />
        </Typography>

        <Box
          sx={{ display: 'flex', flexDirection: 'row', gap: 11.5, mt: 4, justifyContent: 'center' }}
        >
          <InfoCard
            icon={
              <Box
                sx={{
                  backgroundColor: 'primary.light',
                  p: 1.5,
                  borderRadius: 2,
                  display: 'flex',
                }}
              >
                <FileDashedIcon color="#2178EF" size={28} />
              </Box>
            }
            title={t('assessments.onboarding.card1.title')}
            description={t('assessments.onboarding.card1.description')}
          />
          <InfoCard
            icon={
              <Box sx={{ backgroundColor: '#DDFBEB', p: 1.5, borderRadius: 2, display: 'flex' }}>
                <ShieldWarningIcon color="#1B9E5C" size={28} />
              </Box>
            }
            title={t('assessments.onboarding.card2.title')}
            description={t('assessments.onboarding.card2.description')}
          />
          <InfoCard
            icon={
              <Box
                sx={{
                  backgroundColor: '#EDE4FF',
                  p: 1.5,
                  borderRadius: 2,
                  display: 'flex',
                }}
              >
                <PresentationChartIcon color="#8D12FF" size={28} />
              </Box>
            }
            title={t('assessments.onboarding.card3.title')}
            description={t('assessments.onboarding.card3.description')}
          />
        </Box>
        <Button
          variant="outlined"
          sx={{ borderRadius: 4, py: 1.5, px: 3.75, mt: 5 }}
          onClick={() => navigate(routes.assessmentNew())}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '1rem', color: 'white' }}>
              <Trans i18nKey="assessments.onboarding.button.start" />
            </Typography>
            <ArrowRightIcon size={24} style={{ transform: isRtl ? 'scaleX(-1)' : 'none' }} />
          </Box>
        </Button>
        <Typography
          variant="body2"
          sx={{ fontWeight: 400, fontSize: '0.875rem', color: 'text.secondary', mt: 3 }}
        >
          <Trans i18nKey="assessments.onboarding.button.description" />
        </Typography>
        <Box sx={{ width: '100%', height: '1px', backgroundColor: 'divider', mt: 3 }} />
        <Typography
          variant="body2"
          sx={{ fontWeight: 400, fontSize: '0.875rem', color: 'text.secondary', mt: 3 }}
        >
          <Trans i18nKey="assessments.onboarding.footer.title" />
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5, mt: 1.5 }}>
          <FooterInfoItem text={t('assessments.onboarding.footer.item1')} />
          <FooterInfoItem text={t('assessments.onboarding.footer.item2')} />
          <FooterInfoItem text={t('assessments.onboarding.footer.item3')} />
        </Box>
      </Box>
    </Box>
  );
});
